import { useQuery } from "@tanstack/react-query";
import { getSecureUser } from "@/utils/secureStorage";
import { useLoggedInCompany } from "./useCompanyQueries";
import { useDepartmentData } from "./useDepartmentData";

interface ChatPartner {
  id: string;
  displayName: string;
  profilePhoto?: string;
  role: string;
  type: "User" | "Company";
}

interface Employee {
  _id: string;
  id?: string;
  fullName: string;
  name?: string;
  email: string;
  phoneNumber?: string;
  role: string;
  companyId?: string;
  departmentId?: string;
  isActive?: boolean;
  isApproved?: boolean;
  photo?: string;
  profilePhoto?: string;
}

// interface Department {
//   _id: string;
//   id?: string;
//   name: string;
//   description?: string;
//   departmentAdmin?: string;
// }

export const useAvailableChatPartners = () => {
  const user = getSecureUser();
  const { data: companyData, isLoading: companyLoading, error: companyError } = useLoggedInCompany();
  const { data: departmentData } = useDepartmentData(user?.departmentId || "", user?.role === "department_admin");

  return useQuery({
    queryKey: ["availableChatPartners", user?.id, user?.role, companyData?.data?.document?._id],
    queryFn: async (): Promise<ChatPartner[]> => {
      if (!user) {
        return [];
      }

      try {
        const chatPartners: ChatPartner[] = [];

        // For users and department admins, only show the Company as a chat partner
        if (user.role === "employee" || user.role === "department_admin") {
          // Use the companyId from user data to create company partner
          if (user.companyId) {
            chatPartners.push({
              id: user.companyId,
              displayName: "Company",
              profilePhoto: undefined,
              role: "company",
              type: "Company"
            });
          }
        }
        
        // For company admins, show all employees from company data
        else if (user.role === "company_admin") {
          if (companyData?.data?.document?.employees) {
            const employees = companyData.data.document.employees as Employee[];
            const userId = user.id || user._id;
            
            // Add all employees as potential chat partners (excluding self)
            employees.forEach((employee: Employee) => {
              const employeeId = employee._id || employee.id;
              
              if (employeeId !== userId) {
                  chatPartners.push({
                    id: employeeId || "",
                    displayName: employee.fullName || employee.name || "Unknown User",
                    profilePhoto: employee.profilePhoto || employee.photo,
                    role: employee.role || "employee",
                    type: "User"
                  });
              }
            });
          }
        }


        return chatPartners;
      } catch (error) {
        console.error("Error fetching available chat partners:", error);
        return [];
      }
    },
    enabled: !!user && !companyLoading,
    // Use global defaults for staleTime
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
  });
};